import React, {useEffect, useMemo} from 'react';
import {ComboBox} from '@forecast-it/design-system';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../hooks/useForecastFetchQuery';
import Util from '../../util/util';
import type {PortalApproverPickerQuery} from './__generated__/PortalApproverPickerQuery.graphql';

const approverQuery = graphql`
	query PortalApproverPickerQuery {
		viewer {
			company {
				allPersons(first: 10000, onlyActive: true, excludeClientUsers: true) {
					edges {
						node {
							id
							fullName
						}
					}
				}
			}
		}
	}
`;

type ApproverOption = {id: string; name: string};

export type PortalApproverPickerProps = {
	/** The selected approver as a raw Forecast person id, or null for none. */
	value: number | null;
	disabled?: boolean;
	onChange: (personId: number | null) => void;
	placeholder?: string;
	ariaLabel?: string;
	dataCy?: string;
};

/**
 * The client workspace stores a sign-off approver as a raw Forecast person id (a
 * soft int link) while the person list speaks Relay global ids. Decoding lets a
 * persisted approver be matched back to its entry in the list, and keeps the value
 * handed to `onChange` in the raw form the caller persists.
 */
const decodePersonId = (globalId: string): number | null => {
	const raw = Number(Util.getUUIdFromBase64String(globalId));
	return Number.isFinite(raw) ? raw : null;
};

/**
 * Picks the internal person who approves client sign-off, for a phase or a task row.
 *
 * The people list is fetched on demand (once, on mount) rather than through the host
 * view's own query, so the picker can be dropped into any row without that view
 * having to carry a person list in its fragment.
 *
 * `value` and `onChange` speak raw Forecast person ids, not Relay global ids.
 */
const PortalApproverPicker = ({
	value,
	disabled,
	onChange,
	placeholder,
	ariaLabel,
	dataCy,
}: PortalApproverPickerProps): React.JSX.Element => {
	const {data, fetch} = useForecastFetchQuery<PortalApproverPickerQuery['response'], Record<string, never>>(approverQuery);

	// Fetch once on mount — the approver list takes no variables, so it never refetches.
	useEffect(() => {
		fetch();
	}, []);

	const approverOptions: ApproverOption[] = useMemo(
		() =>
			data?.viewer?.company?.allPersons?.edges?.flatMap(edge =>
				edge?.node ? [{id: edge.node.id, name: edge.node.fullName || ''}] : []
			) ?? [],
		[data]
	);

	// Until the list lands there is no global id to select, so the ComboBox shows the
	// placeholder and picks the persisted approver up once the fetch resolves.
	const selectedGlobalId = value == null ? null : approverOptions.find(p => decodePersonId(p.id) === value)?.id ?? null;

	return (
		<ComboBox
			defaultItems={approverOptions}
			value={selectedGlobalId ?? undefined}
			onChange={(globalId: string | null) => onChange(globalId ? decodePersonId(globalId) : null)}
			disabled={disabled}
			placeholder={placeholder}
			aria-label={ariaLabel}
			width={'flex'}
			data-testid={'portal-approver-picker'}
			data-cy={dataCy}
		>
			{(person: ApproverOption) => (
				<ComboBox.Item key={person.id} textValue={person.name}>
					{person.name}
				</ComboBox.Item>
			)}
		</ComboBox>
	);
};

export default PortalApproverPicker;
